import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserCoach } from '../../lib/auth';
import {
  buildQuantitativeSnapshot, createReport, fetchReportsForRespondent,
  deleteReport, type ReportType, type TransformationReport,
} from '../../lib/transformation-reports';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ArrowLeft, Plus, Trash2, Eye, FileText, Calendar, BarChart2 } from '../../components/icons';

const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  programme_end: 'Programme End',
};

const REPORT_TYPE_COLORS: Record<ReportType, string> = {
  weekly: 'bg-blue-100 text-blue-700',
  monthly: 'bg-sage-100 text-sage-700',
  programme_end: 'bg-amber-100 text-amber-700',
};

function defaultDateRange(type: ReportType): { start: string; end: string } {
  const today = new Date();
  const end = today.toISOString().split('T')[0];
  const start = new Date(today);
  if (type === 'weekly') start.setDate(today.getDate() - 7);
  else if (type === 'monthly') start.setMonth(today.getMonth() - 1);
  else start.setMonth(today.getMonth() - 3);
  return { start: start.toISOString().split('T')[0], end };
}

export function TransformationReports() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [reports, setReports] = useState<TransformationReport[]>([]);
  const [respondentName, setRespondentName] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    reportType: 'monthly' as ReportType,
    start: '',
    end: '',
    title: '',
  });

  useEffect(() => {
    if (!userId) return;
    const { start, end } = defaultDateRange('monthly');
    setForm(f => ({ ...f, start, end }));
  }, [userId]);

  useEffect(() => {
    if (!user || !userId) return;
    const init = async () => {
      const isCoach = await isUserCoach(user.id);
      if (!isCoach) { navigate('/coach'); return; }

      const [{ data: userData }, reportData] = await Promise.all([
        (supabase as any).from('users').select('full_name, email').eq('id', userId).maybeSingle(),
        fetchReportsForRespondent(user.id, userId),
      ]);

      setRespondentName(userData?.full_name || userData?.email || userId);
      setReports(reportData);
      setIsLoading(false);
    };
    init();
  }, [user, userId, navigate]);

  const handleTypeChange = (type: ReportType) => {
    const { start, end } = defaultDateRange(type);
    setForm(f => ({
      ...f,
      reportType: type,
      start,
      end,
      title: f.title || '',
    }));
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !userId) return;
    setGenerating(true);
    try {
      const periodStart = new Date(form.start);
      const periodEnd = new Date(form.end);
      const title = form.title.trim() ||
        `${REPORT_TYPE_LABELS[form.reportType]} Report — ${periodStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} to ${periodEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;

      const quantitative = await buildQuantitativeSnapshot(userId, periodStart, periodEnd);
      const report = await createReport(user.id, userId, form.reportType, periodStart, periodEnd, title, quantitative);

      if (!report) throw new Error('Failed to create report');

      setReports(prev => [{ ...report }, ...prev]);
      setShowForm(false);
      navigate(`/coach/proof/${userId}/reports/${report.id}`);
    } catch (err) {
      console.error('Report generation failed', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    await deleteReport(id);
    setReports(prev => prev.filter(r => r.id !== id));
    setDeletingId(null);
  };

  if (isLoading) return <LoadingSpinner message="Loading reports..." />;

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      <div className="mb-6">
        <Link to={`/coach/results/${userId}`} className="flex items-center gap-1.5 text-sm text-chocolate-500 hover:text-chocolate-700 mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to {respondentName}
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <BarChart2 className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h1 className="freud-heading-xl text-chocolate-900">Transformation Reports</h1>
              <p className="text-sm text-chocolate-500">{respondentName}</p>
            </div>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="freud-btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Generate Report
          </button>
        </div>
      </div>

      {showForm && (
        <div className="freud-card-elevated p-6 mb-6">
          <h2 className="freud-heading-lg text-chocolate-900 mb-4">Generate New Report</h2>
          <form onSubmit={handleGenerate} className="space-y-4">
            <div>
              <label className="freud-label">Report Type</label>
              <div className="flex gap-2 mb-2">
                {(['weekly', 'monthly', 'programme_end'] as ReportType[]).map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => handleTypeChange(t)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                      form.reportType === t
                        ? 'border-sage-500 bg-sage-50 text-sage-700'
                        : 'border-chocolate-200 text-chocolate-600 hover:border-chocolate-400'
                    }`}
                  >
                    {REPORT_TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
              {form.reportType === 'weekly' && (
                <p className="text-xs text-chocolate-400">Covers recent activity, habits, check-ins, and quick wins from the past week.</p>
              )}
              {form.reportType === 'monthly' && (
                <p className="text-xs text-chocolate-400">Focuses on monthly progress — habits, tasks, check-in trends, pathway activity, and next steps.</p>
              )}
              {form.reportType === 'programme_end' && (
                <p className="text-xs text-amber-600">Set the start and end dates to cover the entire coaching programme. This report summarises the full journey from baseline to final status.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="freud-label">Period Start</label>
                <input
                  type="date"
                  className="freud-input"
                  value={form.start}
                  onChange={e => setForm(f => ({ ...f, start: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="freud-label">Period End</label>
                <input
                  type="date"
                  className="freud-input"
                  value={form.end}
                  onChange={e => setForm(f => ({ ...f, end: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div>
              <label className="freud-label">Report Title (optional)</label>
              <input
                className="freud-input"
                placeholder="Auto-generated if left blank"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={generating} className="freud-btn-primary flex items-center gap-2">
                {generating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Generating...
                  </>
                ) : 'Generate Report'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="freud-btn-secondary">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {reports.length === 0 ? (
        <div className="freud-card-elevated p-12 text-center">
          <FileText className="w-12 h-12 text-cream-400 mx-auto mb-3" />
          <p className="text-chocolate-500">No reports yet. Generate the first transformation report for {respondentName}.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map(report => (
            <div key={report.id} className="freud-card-elevated p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${REPORT_TYPE_COLORS[report.report_type]}`}>
                    {REPORT_TYPE_LABELS[report.report_type]}
                  </span>
                  {report.is_published && (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Published</span>
                  )}
                  {report.ai_narrative && (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">AI Narrative</span>
                  )}
                </div>
                <p className="font-medium text-chocolate-900 truncate">{report.title}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-chocolate-400">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(report.period_start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} –{' '}
                    {new Date(report.period_end).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span>Created {new Date(report.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Link
                  to={`/coach/proof/${userId}/reports/${report.id}`}
                  className="p-2 rounded-lg text-chocolate-400 hover:bg-sage-50 hover:text-sage-600 transition-colors"
                  title="View report"
                >
                  <Eye className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => handleDelete(report.id)}
                  disabled={deletingId === report.id}
                  className="p-2 rounded-lg text-chocolate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                  title="Delete report"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
